/**
 * Return value of string path with object
 * @param {Object} obj {gsc:{impression:value, ...}}
 * @param {String} path 'gsc.impression.value
 * @returns value from object with object path
 */
export const stringToObjectPath = ( obj, path ) =>
{
  // init return object with obj
  let current = obj;

  // split path on dot
  const paths = path.split('.');

  for ( let i = 0 ; i < paths.length ; ++i ) 
  {
    if ( 
      current !== null 
      && current[paths[i]] !== null 
      && current !== undefined
      && current[paths[i]] !== undefined 
    )
      current = current[paths[i]];
    else
      return null;
  }

  return current;
}

/**
 * 
 * @param {Array} array Array to sort
 * @param {String} objectPath String with object path to the value to sort
 * @param {String} type string or number
 * @param {String} order sort order with asc or desc
 * @returns Sorted Array
 */
export const sortArrayByObjectField = ( array, objectPath, type = 'string', order = 'asc' ) => 
{
  if( Array.isArray( array ) )
  {
    if(
      typeof objectPath === 'string'
      && ( type === 'string' || type === 'number' )
      && ( order === 'asc' || order === 'desc' )
    ){
      array = array.sort( ( a, b ) => 
        order === 'asc' ?
          type === 'number' ? 
            stringToObjectPath( a, objectPath ) - stringToObjectPath( b, objectPath )
          :
            stringToObjectPath( a, objectPath ).localeCompare( stringToObjectPath( b, objectPath ) )
        : 
          type === 'number' ? 
            stringToObjectPath( b, objectPath ) - stringToObjectPath( a, objectPath )
          :
            stringToObjectPath( b, objectPath ).localeCompare( stringToObjectPath( a, objectPath ) )
      )
    }
  }

  return array;
}

/**
 * Return min / max values for all properties of object in array AND global min / max values
 * @param {Array} datas 
 * @param {Array} fields 
 * @returns {Object}
 */
export const getMinMaxFromArrayObject = ( datas, fields ) => 
{
  let result = {};

  if( Array.isArray( datas ) && Array.isArray( fields ) )
  {
    let globalMinMax = { min: [], max: [] };

    // add min / max for each field
    fields.forEach( field => 
    {
      // get min / max values for field
      const min = Math.min( ...datas.map( data => stringToObjectPath( data, field ) ).filter( elem => elem !== null && !isNaN( elem ) ) );
      const max = Math.max( ...datas.map( data => stringToObjectPath( data, field ) ).filter( elem => elem !== null && !isNaN( elem ) ) );

      // add values for field
      result[field] = { min: min, max: max };

      // add values for global
      globalMinMax.min = [...globalMinMax.min, min];
      globalMinMax.max = [...globalMinMax.max, max];
    });

    // add min / max for all fields
    result.global = {
      min: Math.min( ...globalMinMax.min ),
      max: Math.max( ...globalMinMax.max )
    };
  }

  return result;
}

export const getMinMaxFromStackedArrayObject = ( datas, fields ) => 
{
  let result = {};

  if( Array.isArray( datas ) && Array.isArray( fields ) )
  {
    // get values from each date
    const reduceDatas = datas.map( data => 
    {
      // filter data on fields
      const filteredData = Object.fromEntries( Object.entries( data ).filter( ( [k] ) => fields.includes( k ) ) );

      // return reduce data
      return Object.values( filteredData ).reduce( ( a, b ) => typeof b === 'number'  ? ( a + b ) : a, 0 );
    });

    result.min = Math.min( ...reduceDatas );
    result.max = Math.max( ...reduceDatas );
  }

  return result;
}

export const getTopXFromArrayObject = ( datas, x ) => 
{
  // get all keys
  let keys = [];
  if( Array.isArray( datas ) )
  {
    datas.forEach( data => keys = [...keys, ...Object.keys( data ) ] );

    // remove duplicate entries
    keys = [...new Set(keys)];
  }

  // get sum of each key
  const values = keys.map( key => ({ key: key, value: getSumFromArrayOfObject( datas, key ) }) );
  
  // sort value by value
  const sortValues = sortArrayByObjectField( values, 'value', 'number', 'desc');
  
  // limit array with X value
  return sortValues.slice( 0, x );
}

/**
 * Filter datas object with Top X from y values sum
 * @param {Object} datas 
 * @param {Integer} x 
 * @returns Object
 */
export const filterTopXFromArrayObject = ( datas, x ) => 
{
    // get all keys
  const keys = Object.keys( datas );

  // get sum of each key
  const values = keys.map( key => ({ key: key, value: getSumFromArrayOfObject( datas[key], 'y' ) }) );
  
  // sort value by value
  const sortValues = sortArrayByObjectField( values, 'value', 'number', 'desc');
  
  // limit array with X value
  const limitKeys = sortValues.slice( 0, x ).map( value => value.key );

  // return all datas from limit keys
  return Object.fromEntries( Object.entries( datas ).filter( ( [k] ) => limitKeys.includes( k ) ) );
}

/**
 * Return sum of all element from object with key
 * @param {Array} array
 * @param {String} key 
 * @returns {Number}
 */
export const getSumFromArrayOfObject = ( array, key ) => 
{
  let sum = null;
  if( Array.isArray( array ) && array.length > 0 )
    sum = array.map( value => value[key] ).filter( elem => elem !== null ).reduce( ( accumulator, currentValue ) => accumulator + currentValue, 0 );

  return sum;
}

/**
 * Return average of all element from object with key
 * @param {Array} array 
 * @param {String} key 
 * @returns {Number}
 */
export const getAvgFromArrayOfObject = ( array, key ) => 
{
  let avg = null;
  if( Array.isArray( array ) && array.length > 0 )
  {
    // get sum
    const sum = getSumFromArrayOfObject( array, key );
  
    // get length
    const length = array.map( value => value[key] ).filter( elem => elem !== null ).length;

    if( length > 0 )
      avg = sum / length;    
  } 

  return avg;
}

/**
 * Return value of associated key after getting most recent (max) value of key
 * @param {Array} array 
 * @param {String} key the key to get most recent paire (max)
 * @param {String} associatedKey the key to get most recent value
 * @param {Boolean} notNull boolean to keep or remove null values
 * @returns Any
 */
export const getMostRecentFromArrayObject = ( array, key, associatedKey, notNull = false ) =>
{
  let recently = null;

  // remove null values
  if( notNull )
    array = array.filter( elem => elem[associatedKey] !== null );

  // get max key value
  const maxValue = getMinMaxFromArrayObject( array, [key] );

  // get value with recently
  if( array.length > 0 && maxValue[key]?.max !== undefined )
    recently = array.find( elem => elem[key] === maxValue[key].max )[associatedKey];

  return recently;
}

/*!
 * Deep merge two or more objects or arrays.
 * (c) 2023 Chris Ferdinandi, MIT License, https://gomakethings.com
 * @param   {*} ...objs  The arrays or objects to merge
 * @returns {*}          The merged arrays or objects
 */
export const deepMergeObj = ( ...objs ) => 
{
	/**
	 * Get the object type
	 * @param  {*}       obj The object
	 * @return {String}      The object type
	 */
	function getType (obj) {
		return Object.prototype.toString.call(obj).slice(8, -1).toLowerCase();
	}

	/**
	 * Deep merge two objects
	 * @return {Object}
	 */
	function mergeObj (clone, obj) {
		for (let [key, value] of Object.entries(obj)) {
			let type = getType(value);
			if (clone[key] !== undefined && getType(clone[key]) === type && ['array', 'object'].includes(type)) {
				clone[key] = deepMergeObj(clone[key], value);
			} else {
				clone[key] = structuredClone(value);
			}
		}
	}

	// Create a clone of the first item in the objs array
	let clone = structuredClone(objs.shift());

	// Loop through each item
	for (let obj of objs) {

		// Get the object type
		let type = getType(obj);

		// If the current item isn't the same type as the clone, replace it
		if (getType(clone) !== type) {
			clone = structuredClone(obj);
			continue;
		}

		// Otherwise, merge
		if (type === 'array') {
			clone = [...clone, ...structuredClone(obj)];
		} else if (type === 'object') {
			mergeObj(clone, obj);
		} else {
			clone = obj;
		}

	}

	return clone;
}

/**
 * Sort object keys order by pattern array values
 * @param {object} object 
 * @param {array} pattern 
 * @returns object
 */
export const sortObjectKeysByPattern = ( object, pattern ) => 
{
  let result = {};
  pattern.forEach( key => {
    if( object[key] )
      result[key] = object[key]
  });

  return result;
}

/**
 * Update params in current url
 * @param {Object} params 
 * @param {Array} [ searchParams, setSearchParams ] From useSearchParams
 */
export const updateURLSearchParams = ( params, [ searchParams, setSearchParams ] ) => 
{
  if( typeof params === 'object' )
  {
    Object.keys( params ).forEach( paramName => 
    {
      if( params[paramName] !== null )
        searchParams.set( paramName, params[paramName] );
      else
        searchParams.delete( paramName );
    });

    setSearchParams( searchParams );
  }
}

/**
 * Return true or false of valid or unvalid JSON string
 * @param {String} str 
 * @returns Boolean
 */
export const isValidJSON = str =>
{
  try {
    JSON.parse(str);
    return true;
  } catch (e) {
    return false;
  }
};

/**
 * Compare 2 objects with deep values
 * @param {Object} object1 
 * @param {Object} object2 
 * @returns Boolean
 */
export const deepEqual = ( object1, object2 ) =>
{
  if( object1 !== null && object2 !== null )
  {
    const keys1 = Object.keys( object1 );
    const keys2 = Object.keys( object2 );
  
    // check keys lenght
    if ( keys1.length !== keys2.length )
      return false;
  
    // check content values
    for ( const key of keys1 ) 
    {
      const val1 = object1[key];
      const val2 = object2[key];
      const areObjects = ( val1 != null && typeof val1 === 'object' ) && ( val2 != null && typeof val2 === 'object' );
  
      if (
        ( areObjects && !deepEqual( val1, val2 ) )
          || 
        ( !areObjects && val1 !== val2 )
      )
        return false;
    }
  
    return true;

  } else
    return false;
}

/**
 * Rewrite value with - instead of space and lowercase
 * @param {string} value 
 * @returns {string}
 */
export const rewriteStringForCSS = value => 
{
  if( typeof value === 'string' )
  {
    // replace space wuth -
    value = value.replace( ' ', '-' );

    // lowercase
    value = value.toLowerCase();
  }

  return value;
}

/**
 * Check if var is null or undefined
 * @param {mixed} value 
 * @returns {boolean}
 */
export const isNullOrUndefined = value => value === undefined || value === null;

/**
 * Remove domain from url string
 * @param {string} value 
 * @returns {string}
 */
export const getUrlPart = ( url, partName ) => 
{
  let result = url;

  if( typeof url === 'string' && url.trim() !== '' )
  {
    // instance new url
    try 
    {
      const urlObj = new URL( url );

      // get part
      if( !isNullOrUndefined( urlObj[ partName ] ) )
        result = urlObj[ partName ];

    } catch ( error ) {
      console.error( error );
    }    
  }

  return result;
};

/**
 * Filter Object on keys with array of strings
 * @param {Object} objectSource 
 * @param {Array} filterValues 
 * @returns 
 */
export const filterObjectOnKeys = ( objectSource, filterValues ) => 
{
  let response = objectSource;

  if( Array.isArray( filterValues ) )
  {
    response = filterValues.reduce( ( obj, key ) => 
    {
      if( !isNullOrUndefined( objectSource[key] ) )
        obj[key] = objectSource[key];
      
      return obj;
    }, {} );
  }

  return response;
}